import Mention from '@tiptap/extension-mention';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTemplateContextFieldsQuery } from '@/services/api/templatesV2ServiceEnhanced';

import { MergeFieldExtension } from './merge-field-extension';
import { createMergeFieldSuggestion } from './merge-field-suggestion';
import type { FieldMeta } from './serialization';
import { jinjaToHtml, tiptapToJinja } from './serialization';

interface HtmlTemplateEditorProps {
  initialBody: string;
  onBodyChange: (body: string) => void;
  readOnly?: boolean;
}

export const HtmlTemplateEditor = ({
  initialBody,
  onBodyChange,
  readOnly = false,
}: HtmlTemplateEditorProps) => {
  const { data: contextFields } = useTemplateContextFieldsQuery();
  const onChangeRef = useRef(onBodyChange);
  onChangeRef.current = onBodyChange;

  // Build key→{label,sample} map so loaded {{ varName }} tokens get display metadata
  const fieldMap = useMemo(() => {
    if (!contextFields) return undefined;
    const map: Record<string, FieldMeta> = {};
    contextFields.categories.forEach((cat) => {
      cat.fields.forEach((f) => {
        map[f.key] = { label: f.label, sample: f.sample };
      });
    });
    return map;
  }, [contextFields]);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          // Disable code-related extensions for template editing
          code: false,
          codeBlock: false,
        }),
        MergeFieldExtension,
        ...(contextFields
          ? [
              Mention.configure({
                suggestion: createMergeFieldSuggestion(
                  contextFields.categories,
                ) as any,
              }),
            ]
          : []),
      ],
      content: jinjaToHtml(initialBody, fieldMap),
      editable: !readOnly,
      onUpdate: ({ editor: ed }) => {
        const jinja = tiptapToJinja(ed.getJSON());
        onChangeRef.current(jinja);
      },
    },
    [contextFields, fieldMap, readOnly],
  );

  // Sync readOnly state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  const handleToolbar = useCallback(
    (action: string) => {
      if (!editor) return;
      const actions: Record<string, () => void> = {
        bold: () => editor.chain().focus().toggleBold().run(),
        italic: () => editor.chain().focus().toggleItalic().run(),
        h2: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
        h3: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
        bulletList: () => editor.chain().focus().toggleBulletList().run(),
        orderedList: () => editor.chain().focus().toggleOrderedList().run(),
      };
      actions[action]?.();
    },
    [editor],
  );

  if (!editor) return null;

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      {!readOnly && (
        <div className="flex items-center gap-0.5 border-b border-zinc-200 px-2 py-1.5 dark:border-zinc-700">
          <ToolbarButton
            label="B"
            active={editor.isActive('bold')}
            onClick={() => handleToolbar('bold')}
            className="font-bold"
          />
          <ToolbarButton
            label="I"
            active={editor.isActive('italic')}
            onClick={() => handleToolbar('italic')}
            className="italic"
          />
          <div className="mx-1 h-5 w-px bg-zinc-200 dark:bg-zinc-700" />
          <ToolbarButton
            label="H2"
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => handleToolbar('h2')}
          />
          <ToolbarButton
            label="H3"
            active={editor.isActive('heading', { level: 3 })}
            onClick={() => handleToolbar('h3')}
          />
          <div className="mx-1 h-5 w-px bg-zinc-200 dark:bg-zinc-700" />
          <ToolbarButton
            label="UL"
            active={editor.isActive('bulletList')}
            onClick={() => handleToolbar('bulletList')}
          />
          <ToolbarButton
            label="OL"
            active={editor.isActive('orderedList')}
            onClick={() => handleToolbar('orderedList')}
          />
          <div className="mx-1 h-5 w-px bg-zinc-200 dark:bg-zinc-700" />
          <span className="ml-auto text-[11px] font-medium text-zinc-500">
            Type <kbd className="rounded border px-1 py-0.5 text-[10px]">{'{'}</kbd> to insert variable
          </span>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1 overflow-y-auto p-4">
        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none dark:prose-invert focus:outline-none [&_.ProseMirror]:min-h-[200px] [&_.ProseMirror]:outline-none"
        />
      </div>
    </div>
  );
};

const ToolbarButton = ({
  label,
  active,
  onClick,
  className = '',
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
      active
        ? 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300'
        : 'text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
    } ${className}`}
  >
    {label}
  </button>
);
